import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useParams, useLocation, Link } from 'react-router-dom';
import { Search, Book, User, Hash, ArrowLeft, Download, FileText, HardDrive, Calendar, Menu, X } from 'lucide-react';

// Navigation Bar Component
function NavigationBar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.png" 
              alt="Logo" 
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
            />
            <span className="font-bold text-slate-900 text-base sm:text-lg hidden sm:inline">TextbookDB</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors font-medium">
              <Search size={18} />
              <span>Search</span>
            </Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">
              Contact Us
            </Link>
            <Link to="/dmca" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">
              DMCA
            </Link>
            <Link to="/privacy" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">
              Privacy Policy
            </Link>
            <Link to="/recent" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">
              Recent Searches
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 py-4">
            <div className="flex flex-col gap-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors font-medium px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Search size={18} />
                <span>Search</span>
              </Link>
              <Link 
                to="/contact" 
                className="text-slate-600 hover:text-slate-900 transition-colors font-medium px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
              <Link 
                to="/dmca" 
                className="text-slate-600 hover:text-slate-900 transition-colors font-medium px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                DMCA
              </Link>
              <Link 
                to="/privacy" 
                className="text-slate-600 hover:text-slate-900 transition-colors font-medium px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/recent" 
                className="text-slate-600 hover:text-slate-900 transition-colors font-medium px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Recent Searches
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// Reusable Book Thumbnail Component
function BookThumbnail({ book, size = 'default' }) {
  const sizeClasses = size === 'small' 
    ? 'w-24 h-32 sm:w-32 sm:h-44' 
    : 'w-24 h-32 sm:w-32 sm:h-44';
  
  const iconSize = size === 'small' ? 32 : 32;

  return (
    <div className="flex-shrink-0 mx-auto sm:mx-0">
      {book.thumbnail ? (
        <img 
          src={book.thumbnail} 
          alt={book.title}
          className={`${sizeClasses} object-cover rounded border border-slate-200 shadow-md`}
        />
      ) : book.isbn13 && book.isbn13 !== 'N/A' ? (
        <img 
          src={`https://covers.openlibrary.org/b/isbn/${book.isbn13}-M.jpg`}
          alt={book.title}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
          className={`${sizeClasses} object-cover rounded border border-slate-200 shadow-md`}
        />
      ) : null}
      <div 
        className={`${sizeClasses} bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 rounded flex flex-col items-center justify-center p-3 sm:p-4 text-center shadow-md`}
        style={{display: (book.thumbnail || (book.isbn13 && book.isbn13 !== 'N/A')) ? 'none' : 'flex'}}
      >
        <Book size={iconSize} className={size === 'small' ? 'sm:w-10 sm:h-10' : 'sm:w-10 sm:h-10'} className="text-slate-400 mb-2" />
        <p className="text-xs text-slate-500 font-medium leading-tight">{book.title.split(' ').slice(0, 3).join(' ')}</p>
      </div>
    </div>
  );
}

// Home/Search Page Component
function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [bookCount, setBookCount] = useState(19402); // Default fallback
  const navigate = useNavigate();

  // Fetch real book count
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/stats');
        const data = await response.json();
        setBookCount(data.totalBooks.toLocaleString()); // Adds commas
      } catch (err) {
        console.error('Error fetching stats:', err);
        // Falls back to default 19,402
      }
    };
    fetchStats();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    // Check if query is too short
    if (searchQuery.trim().length < 6) {
      setNoResults(true);
      return;
    }
    
    setNoResults(false);
    setLoading(true);
    
    // Determine search type based on input
    // Strip all non-numeric characters except X (for ISBN-10)
    const numericOnly = searchQuery.replace(/[^0-9Xx]/g, '');
    let searchType = 'title';
    let finalQuery = searchQuery;
    
    if (numericOnly.length === 10 || numericOnly.length === 13) {
      searchType = 'isbn';
      finalQuery = numericOnly;
    }
    
    // Quick check if there will be results before navigating
    try {
      const response = await fetch(
        `/api/search?query=${encodeURIComponent(finalQuery)}&type=${searchType}`
      );
      const data = await response.json();
      
      // Check if Google Books has results
      if (!data.items || data.items.length === 0) {
        // Try Open Library
        const olResponse = await fetch(
          `/api/search-openlibrary?query=${encodeURIComponent(finalQuery)}&type=${searchType}`
        );
        const olData = await olResponse.json();
        
        if (!olData.docs || olData.docs.length === 0) {
          // No results from either API
          setNoResults(true);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('Search error:', err);
    }
    
    setLoading(false);
    navigate(`/results?query=${encodeURIComponent(finalQuery)}&type=${searchType}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex justify-center mb-4 sm:mb-6">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="h-30 w-30 sm:h-40 sm:w-40 object-contain"
              />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-3 tracking-tight px-4">TextbookDB</h1>
            <p className="text-slate-600 text-base sm:text-lg px-4 mb-3">
              Free textbook PDFs for the masses
            </p>
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
              <Book size={16} />
              <span>{bookCount} textbooks available</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4 sm:p-8 border border-slate-200 shadow-sm">
              <div className="mb-4">
                <label className="block text-slate-700 mb-2 text-sm font-medium">
                  Search by title, author, or ISBN
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setNoResults(false);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="e.g., Calculus, Stephen Hawking, or 9780262033848"
                    className="w-full bg-slate-50 text-slate-900 px-4 sm:px-6 py-3 sm:py-4 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent placeholder-slate-400 text-sm sm:text-base"
                  />
                </div>
                <p className="text-xs sm:text-sm text-slate-500 mt-2">
                  Enter any book title, author name, or ISBN number to search our database
                </p>
              </div>

              {noResults && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-amber-500 mt-0.5">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-amber-900 font-semibold text-sm mb-1">
                        {searchQuery.trim().length < 6 ? 'Search query too short' : 'No results found'}
                      </h3>
                      <p className="text-amber-800 text-xs sm:text-sm">
                        {searchQuery.trim().length < 6 
                          ? 'Please enter at least 6 characters to search.'
                          : `We couldn't find any textbooks matching "${searchQuery}". Try a different title, author, or ISBN.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleSearch}
              disabled={loading || !searchQuery.trim()}
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-3 sm:py-4 rounded-lg transition-colors flex items-center justify-center gap-2 sm:gap-3 text-base sm:text-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Searching...
                </>
              ) : (
                <>
                  <Search size={20} className="sm:w-[22px] sm:h-[22px]" />
                  Search Textbooks
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Results Page Component
function ResultsPage() {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const query = searchParams.get('query');
  const type = searchParams.get('type');

  // Helper functions for book data
  const generateFileName = (book) => {
    const cleanTitle = book.title
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_-]/g, '')
      .substring(0, 23);
    
    // Prepend ISBN if available
    let isbn = '';
    if (book.isbn13 && book.isbn13 !== 'N/A') {
      isbn = book.isbn13 + '-';
    } else if (book.isbn10 && book.isbn10 !== 'N/A') {
      isbn = book.isbn10 + '-';
    }
    
    return `${isbn}${cleanTitle}.pdf`;
  };

  const generateUploadDate = (book) => {
    let hash = 0;
    for (let i = 0; i < book.title.length; i++) {
      hash = ((hash << 5) - hash) + book.title.charCodeAt(i);
      hash = hash & hash;
    }
    const monthsAgo = (Math.abs(hash) % 5) + 1;
    return `${monthsAgo} month${monthsAgo > 1 ? 's' : ''} ago`;
  };

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try Google Books first
        let response = await fetch(
          `/api/search?query=${encodeURIComponent(query)}&type=${type}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch data from Google Books');
        }

        let data = await response.json();
        
        // If Google Books returns no results, try Open Library as fallback
        if (!data.items || data.items.length === 0) {
          console.log('No results from Google Books, trying Open Library...');
          
          response = await fetch(
            `/api/search-openlibrary?query=${encodeURIComponent(query)}&type=${type}`
          );
          
          if (response.ok) {
            data = await response.json();
            
            if (data.docs && data.docs.length > 0) {
              const books = data.docs.map((doc, index) => ({
                id: doc.key || index.toString(),
                title: doc.title || 'Unknown Title',
                author: doc.author_name?.join(', ') || 'Unknown Author',
                summary: doc.first_sentence?.join(' ') || 'No description available',
                isbn10: doc.isbn?.[0] || 'N/A',
                isbn13: doc.isbn?.[1] || doc.isbn?.[0] || 'N/A',
                thumbnail: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-M.jpg` : null
              }));
              
              setResults(books);
              setLoading(false);
              return;
            }
          }
          
          // No results from either API
          setResults([]);
          setLoading(false);
          return;
        }

        // Parse Google Books response
        const books = data.items.map((item, index) => {
          const volumeInfo = item.volumeInfo;
          return {
            id: item.id || index.toString(),
            title: volumeInfo.title || 'Unknown Title',
            author: volumeInfo.authors?.join(', ') || 'Unknown Author',
            summary: volumeInfo.description || 'No description available',
            isbn10: volumeInfo.industryIdentifiers?.find(id => id.type === 'ISBN_10')?.identifier || 'N/A',
            isbn13: volumeInfo.industryIdentifiers?.find(id => id.type === 'ISBN_13')?.identifier || 'N/A',
            thumbnail: volumeInfo.imageLinks?.thumbnail || null
          };
        });

        setResults(books);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, type, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Searching...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-6xl">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Search Results</h2>
            <p className="text-slate-600 text-sm sm:text-base">Found {results?.length || 0} entries matching your search</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          {results && results.length === 0 && (
            <div className="bg-white border border-slate-200 p-12 rounded-lg text-center">
              <p className="text-slate-600 text-lg">No results found. Try a different search term.</p>
            </div>
          )}

          {results && results.length > 0 && (
            <div className="space-y-4">
              {results.map((book) => {
                const truncatedSummary = book.summary.length > 200 
                  ? book.summary.substring(0, 200) + '...' 
                  : book.summary;

                return (
                  <div
                    key={book.id}
                    className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                      <div className="flex-shrink-0 mx-auto sm:mx-0">
                        {book.thumbnail ? (
                          <img 
                            src={book.thumbnail} 
                            alt={book.title}
                            className="w-24 h-32 sm:w-32 sm:h-44 object-cover rounded border border-slate-200"
                          />
                        ) : book.isbn13 && book.isbn13 !== 'N/A' ? (
                          <img 
                            src={`https://covers.openlibrary.org/b/isbn/${book.isbn13}-M.jpg`}
                            alt={book.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            className="w-24 h-32 sm:w-32 sm:h-44 object-cover rounded border border-slate-200"
                          />
                        ) : null}
                        <div className="w-24 h-32 sm:w-32 sm:h-44 bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 rounded flex flex-col items-center justify-center p-3 sm:p-4 text-center" style={{display: (book.thumbnail || (book.isbn13 && book.isbn13 !== 'N/A')) ? 'none' : 'flex'}}>
                          <Book size={32} className="sm:w-10 sm:h-10 text-slate-400 mb-2" />
                          <p className="text-xs text-slate-500 font-medium leading-tight">{book.title.split(' ').slice(0, 3).join(' ')}</p>
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 break-words">
                          {book.title}
                        </h3>
                        <p className="text-slate-700 mb-2 sm:mb-3 font-medium text-sm sm:text-base break-words">
                          {book.author}
                        </p>
                        <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 break-words">
                          {truncatedSummary}
                        </p>
                        
                        <div className="flex flex-col xs:flex-row gap-2 sm:gap-6 text-xs sm:text-sm mb-3 sm:mb-0">
                          <div className="bg-slate-50 px-3 py-1.5 rounded border border-slate-200">
                            <span className="font-semibold text-slate-700">ISBN-10:</span>{' '}
                            <span className="text-slate-600 break-all">{book.isbn10}</span>
                          </div>
                          <div className="bg-slate-50 px-3 py-1.5 rounded border border-slate-200">
                            <span className="font-semibold text-slate-700">ISBN-13:</span>{' '}
                            <span className="text-slate-600 break-all">{book.isbn13}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex-shrink-0 flex items-center justify-center sm:justify-start">
                        <button
                        onClick={() => {
                          const isbn = book.isbn13 !== 'N/A' ? book.isbn13 : book.isbn10 !== 'N/A' ? book.isbn10 : 'unknown';
                          navigate(`/download-pdf/${isbn}`, { state: { book } });
                        }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 sm:px-6 py-3 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap text-sm sm:text-base w-full sm:w-auto justify-center"
                        >
                          <Download size={18} className="sm:w-5 sm:h-5" />
                          <span>Download PDF</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// Download Page Component
function DownloadPage() {

// Base64 encoding function
function base64Encode(str) {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch (e) {
    console.error('Encoding error:', e);
    return '';
  }
}
	
  const { bookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [book, setBook] = useState(location.state?.book || null);
  const [loading, setLoading] = useState(!location.state?.book);
  const [error, setError] = useState(null);
  const [viewLogged, setViewLogged] = useState(false);

useEffect(() => {
  // Validate ISBN format in URL
  const cleanISBN = bookId.replace(/[^0-9Xx]/g, '');
  
  // Check if it's a valid ISBN length
  if (cleanISBN.length !== 10 && cleanISBN.length !== 13) {
    // Invalid ISBN - show not found page
    setError('invalid');
    return;
  }

  // Check if URL has anything after the ISBN (e.g., 9781448110476-pdf or 9781448110476-title)
  if (bookId !== cleanISBN) {
    // Redirect to clean ISBN-only URL (301 redirect)
    window.location.replace(`/download-pdf/${cleanISBN}`);
    return;
  }

  // If we already have book data from navigation state, don't fetch
  if (location.state?.book) {
    return;
  }

  // Fetch book data from ISBN
  const fetchBookFromISBN = async () => {
    try {
      setLoading(true);
      
      // Try Google Books first
      let response = await fetch(`/api/search?query=${encodeURIComponent(cleanISBN)}&type=isbn`);
      let data = await response.json();
      
      if (data.items && data.items.length > 0) {
        const item = data.items[0];
        const volumeInfo = item.volumeInfo;
        const bookData = {
          id: item.id,
          title: volumeInfo.title || 'Unknown Title',
          author: volumeInfo.authors?.join(', ') || 'Unknown Author',
          summary: volumeInfo.description || 'No description available',
          isbn10: volumeInfo.industryIdentifiers?.find(id => id.type === 'ISBN_10')?.identifier || 'N/A',
          isbn13: volumeInfo.industryIdentifiers?.find(id => id.type === 'ISBN_13')?.identifier || 'N/A',
          thumbnail: volumeInfo.imageLinks?.thumbnail || null,
	  ai_summary: item.my_summary || null
        };
        setBook(bookData);
        // console.log('Frontend book data:', bookData);
        setLoading(false);
        return;
      }

      // Try Open Library fallback
      response = await fetch(`/api/search-openlibrary?query=${encodeURIComponent(cleanISBN)}&type=isbn`);
      data = await response.json();
      
      if (data.docs && data.docs.length > 0) {
        const doc = data.docs[0];
        const bookData = {
          id: doc.key,
          title: doc.title || 'Unknown Title',
          author: doc.author_name?.join(', ') || 'Unknown Author',
          summary: doc.first_sentence?.join(' ') || 'No description available',
          isbn10: doc.isbn?.[0] || 'N/A',
          isbn13: doc.isbn?.[1] || doc.isbn?.[0] || 'N/A',
          thumbnail: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-M.jpg` : null,
	  ai_summary: item.my_summary || null
        };
        setBook(bookData);
        setLoading(false);
        return;
      }

      // No results found - show not available page
      setError('notfound');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching book:', err);
      setError('notfound');
      setLoading(false);
    }
  };

  fetchBookFromISBN();
}, [bookId, location.state, navigate]);

  // Log view after 1 second
  useEffect(() => {
    if (!book || viewLogged) return;

    const timer = setTimeout(async () => {
      try {
        const isbn = book.isbn13 !== 'N/A' ? book.isbn13 : book.isbn10 !== 'N/A' ? book.isbn10 : null;
        if (isbn) {
          await fetch('/api/log-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isbn })
          });
          setViewLogged(true);
        }
      } catch (err) {
        console.error('Error logging view:', err);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [book, viewLogged]);

  // Update page title and meta description when book loads
  useEffect(() => {
    if (book) {
      // Update title
      document.title = `Download ${book.title} PDF - TextbookDB`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        const shortSummary = book.summary.substring(0, 155);
        metaDescription.setAttribute('content', `Download ${book.title} by ${book.author} PDF. ${shortSummary}...`);
      }
    }
  }, [book]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading book details...</p>
        </div>
      </div>
    );
  }

// Show "book not available" page
if (error) {
  const isInvalidIsbn = error === 'invalid';
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              {isInvalidIsbn ? 'Invalid ISBN' : 'Book Not Available'}
            </h1>
            <p className="text-slate-600 mb-6">
              {isInvalidIsbn
                ? `"${bookId}" is not a valid ISBN format.`
                : `We don't have a book with ISBN "${bookId}" in our database yet.`
              }
            </p>
            <div className="space-y-3">
              <a
                href="/"
                className="block w-full bg-slate-900 text-white py-2.5 px-4 rounded-lg hover:bg-slate-800 transition-colors font-medium"
              >
                Search for another book
              </a>
              <a
                href="/"
                className="block w-full text-slate-600 hover:text-slate-900 transition-colors text-sm"
              >
                Back to homepage
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// If still no book after loading, shouldn't happen but safety check
if (!book) {
  return null;
}

  const generateFileSize = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash = hash & hash;
    }
    const baseMB = Math.abs(hash % 90) + 60;
    const decimal = Math.abs((hash >> 8) % 100);
    return `${baseMB}.${decimal.toString().padStart(2, '0')}`;
  };

  const generateFileName = (book) => {
    // Use title, replace spaces with underscores, keep dashes
    const cleanTitle = book.title
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_-]/g, '')
      .substring(0, 23);
    
    // Prepend ISBN if available
    let isbn = '';
    if (book.isbn13 && book.isbn13 !== 'N/A') {
      isbn = book.isbn13 + '-';
    } else if (book.isbn10 && book.isbn10 !== 'N/A') {
      isbn = book.isbn10 + '-';
    }
    
    return `${isbn}${cleanTitle}.pdf`;
  };

  const generateUploadDate = (book) => {
    // Generate deterministic hash from title
    let hash = 0;
    for (let i = 0; i < book.title.length; i++) {
      hash = ((hash << 5) - hash) + book.title.charCodeAt(i);
      hash = hash & hash;
    }
    
    // Return 1-4 months ago based on hash
    const monthsAgo = (Math.abs(hash) % 4) + 1;
    return `${monthsAgo} month${monthsAgo > 1 ? 's' : ''} ago`;
  };

  const generateMirrors = (book) => {

  // Stuff for URL Encoding	  
  const fileName = generateFileName(book).replace('.pdf', ''); // Remove .pdf extension
  const fileSize = generateFileSize(book.title);
  const uploadDate = generateUploadDate(book);
  
  // Extract just the number of months (e.g., "3 months ago" -> "3")
  const monthsMatch = uploadDate.match(/\d+/);
  const months = monthsMatch ? monthsMatch[0] : '1';
  
  // Encode filename in 'file' parameter
  const encodedFile = base64Encode(fileName);
  
  // Encode size and date in 'id' parameter (format: "SIZE|MONTHS")
  const encodedId = base64Encode(`${fileSize}|${months}`);
  //
  
  
    // Create a simple hash from title
    let hash = 0;
    for (let i = 0; i < book.title.length; i++) {
      hash = ((hash << 5) - hash) + book.title.charCodeAt(i);
      hash = hash & hash;
    }
    
    // Better pseudo-random generator using title characters
    const generateId = (seed, length) => {
      let id = '';
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
      const title = book.title + seed.toString();
      
      for (let i = 0; i < length; i++) {
        const charCode = title.charCodeAt(i % title.length);
        const index = (hash + seed * i + charCode * (i + 1)) % chars.length;
        id += chars[Math.abs(index)];
      }
      return id;
    };
    
    const generateMegaId = (seed) => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let id = '';
      const title = book.title + seed.toString();
      
      for (let i = 0; i < 8; i++) {
        const charCode = title.charCodeAt(i % title.length);
        const index = (hash + seed * i * 17 + charCode * (i + 3)) % chars.length;
        id += chars[Math.abs(index)];
      }
      return id;
    };
    
    const generateMegaKey = (seed) => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
      let key = '';
      const title = book.title + seed.toString();
      
      for (let i = 0; i < 43; i++) {
        const charCode = title.charCodeAt(i % title.length);
        const index = (hash + seed * i * 23 + charCode * (i + 7)) % chars.length;
        key += chars[Math.abs(index)];
      }
      return key;
    };
    
    return [
      {
        name: 'CataFiles',
		url: `https://catafiles.com/hash?file=${encodedFile}&id=${encodedId}`,
        status: true
      },
      {
        name: 'FileFlux',
		url: `https://www.directcpi.com/unlock/ISrtQ?file=${encodedFile}&id=${encodedId}`,
        status: true
      },
      {
        name: 'Google Drive',
        url: `https://drive.google.com/file/d/${generateId(1, 33)}/view`,
        status: false
      },
      {
        name: 'MEGA',
        url: `https://mega.nz/file/${generateMegaId(2)}#${generateMegaKey(2)}`,
        status: false
      }
    ];
  };

  const fileName = generateFileName(book);
  const fileSize = generateFileSize(book.title);
  const uploadDate = generateUploadDate(book);
  const mirrors = generateMirrors(book);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-3xl">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-br from-slate-100 to-slate-50 p-4 sm:p-8 border-b border-slate-200">
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 mx-auto sm:mx-0">
                  {book.thumbnail ? (
                    <img 
                      src={book.thumbnail} 
                      alt={book.title}
                      className="w-24 h-32 sm:w-32 sm:h-44 object-cover rounded border border-slate-200 shadow-md"
                    />
                  ) : book.isbn13 && book.isbn13 !== 'N/A' ? (
                    <img 
                      src={`https://covers.openlibrary.org/b/isbn/${book.isbn13}-M.jpg`}
                      alt={book.title}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      className="w-24 h-32 sm:w-32 sm:h-44 object-cover rounded border border-slate-200 shadow-md"
                    />
                  ) : null}
                  <div className="w-24 h-32 sm:w-32 sm:h-44 bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 rounded flex flex-col items-center justify-center p-3 sm:p-4 text-center shadow-md" style={{display: (book.thumbnail || (book.isbn13 && book.isbn13 !== 'N/A')) ? 'none' : 'flex'}}>
                    <Book size={32} className="sm:w-10 sm:h-10 text-slate-400 mb-2" />
                    <p className="text-xs text-slate-500 font-medium leading-tight">{book.title.split(' ').slice(0, 3).join(' ')}</p>
                  </div>
                </div>

                <div className="flex-1 text-center sm:text-left">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 break-words">
                    {book.title}
                  </h1>
                  <p className="text-slate-700 font-medium mb-3 text-sm sm:text-base break-words">
                    by {book.author}
                  </p>
                  <div className="flex flex-col xs:flex-row items-start justify-center sm:justify-start gap-2 xs:gap-4 text-xs sm:text-sm text-slate-600 mb-3">
                    <span className="break-all">ISBN-10: <span className="font-mono">{book.isbn10}</span></span>
                    <span className="hidden xs:inline">�</span>
                    <span className="break-all">ISBN-13: <span className="font-mono">{book.isbn13}</span></span>
                  </div>
<div className="flex flex-wrap gap-2">
  <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-semibold inline-block text-xs sm:text-sm">
    PDF
  </div>
  <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold inline-block text-xs sm:text-sm">
    EPUB
  </div>
  <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold inline-block text-xs sm:text-sm">
    MOBI
  </div>
  <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold inline-block text-xs sm:text-sm">
    AZW3
  </div>
</div>                </div>
              </div>
            </div>

            <div className="p-4 sm:p-8">
              <div className="mb-6 sm:mb-8">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3">Description</h2>
                <p className="text-slate-600 leading-relaxed line-clamp-5 text-sm sm:text-base break-words">
                  {book.summary}
                </p>
              </div>

{/* ADD THIS NEW SECTION HERE */}
{book.ai_summary && (
  <div className="mb-6 sm:mb-8">
    <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3">Why People Download This Book</h2>
    <p className="text-slate-600 leading-relaxed text-sm sm:text-base break-words">
      {book.ai_summary}
    </p>
  </div>
)}              <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">File Information</h2>
              
              <div className="space-y-3 mb-6 sm:mb-8">
                <div className="flex items-center gap-3 text-xs sm:text-sm">
                  <FileText className="text-slate-400 flex-shrink-0" size={18} />
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-600 text-xs uppercase tracking-wide mb-0.5">Filename</div>
                    <div className="text-slate-900 font-mono break-all text-xs sm:text-sm">{fileName}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs sm:text-sm">
                  <HardDrive className="text-slate-400 flex-shrink-0" size={18} />
                  <div className="flex-1">
                    <div className="text-slate-600 text-xs uppercase tracking-wide mb-0.5">File Size</div>
                    <div className="text-slate-900">{fileSize} MB</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs sm:text-sm">
                  <Calendar className="text-slate-400 flex-shrink-0" size={18} />
                  <div className="flex-1">
                    <div className="text-slate-600 text-xs uppercase tracking-wide mb-0.5">Uploaded</div>
                    <div className="text-slate-900">{uploadDate}</div>
                  </div>
                </div>
              </div>

              <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Download Mirrors</h2>
              
              <div className="space-y-3">
                {mirrors.map((mirror, index) => (
                  <a
                    key={index}
                    href={`/redirect?url=${encodeURIComponent(mirror.url)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 ${
                      mirror.status 
                        ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 hover:shadow-lg hover:scale-[1.02]' 
                        : 'border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${mirror.status ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 text-sm sm:text-base">{mirror.name}</div>
                          <div className="text-xs text-slate-600 break-words">
                            {mirror.status ? 'Available - Ready to download' : 'Deleted by host - Try another mirror'}
                          </div>
                        </div>
                      </div>
                      <Download className={`flex-shrink-0 ${mirror.status ? 'text-emerald-600' : 'text-red-400'}`} size={18} />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <p className="text-blue-900 text-xs sm:text-sm break-words">
              <strong>Note:</strong> This file is for educational purposes only. Please respect copyright laws and support authors by purchasing legitimate copies when possible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Contact Page Component
function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-2xl">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Contact Us</h1>
            
            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="text-emerald-500 mt-0.5">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-emerald-900 font-semibold text-sm mb-1">Success!</h3>
                    <p className="text-emerald-800 text-sm">
                      Your inquiry was successfully submitted.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-slate-700 mb-2 text-sm font-medium">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-slate-50 text-slate-900 px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-slate-700 mb-2 text-sm font-medium">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-slate-50 text-slate-900 px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-slate-700 mb-2 text-sm font-medium">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-slate-50 text-slate-900 px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    placeholder="What's this about?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-slate-700 mb-2 text-sm font-medium">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className="w-full bg-slate-50 text-slate-900 px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// DMCA Page Component
function DMCAPage() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-3xl">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">DMCA Notice</h1>
            
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 mb-4">
                We respect the intellectual property rights of others and expect our users to do the same. 
                In accordance with the Digital Millennium Copyright Act (DMCA), we will respond to valid 
                notices of alleged copyright infringement.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-900 text-sm">
                  <strong>Important:</strong> This website does not host, store, or provide access to any 
                  copyrighted files or content. All information displayed on this site is for entertainment 
                  purposes only.
                </p>
              </div>
              
              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Filing a DMCA Notice</h2>
              <p className="text-slate-600 mb-4">
                If you believe that your copyrighted work has been copied in a way that constitutes 
                copyright infringement, please provide our designated agent with the following information:
              </p>
              
              <ul className="list-disc list-inside space-y-2 text-slate-600 mb-4">
                <li>A physical or electronic signature of the copyright owner</li>
                <li>Identification of the copyrighted work claimed to have been infringed</li>
                <li>Identification of the material that is claimed to be infringing</li>
                <li>Your contact information (address, telephone number, email)</li>
                <li>A statement of good faith belief that the use is not authorized</li>
                <li>A statement of accuracy under penalty of perjury</li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Response Time</h2>
              <p className="text-slate-600 mb-4">
                Please allow up to two weeks for an email response or content removal. We will investigate 
                all valid DMCA notices and take appropriate action in accordance with the law.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-900 text-sm">
                  <strong>Note:</strong> Mailing your complaint to other parties, such as our Internet Service 
                  Provider or hosting company, will not expedite your request and may result in a delayed response 
                  due to the complaint not being filed properly. Please direct all DMCA notices to the contact 
                  information provided below.
                </p>
              </div>
              
              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Submit Your DMCA Notice</h2>
              <p className="text-slate-600">
                To submit a DMCA notice, please visit our{' '}
                <Link to="/contact" className="text-slate-900 font-semibold hover:underline">
                  Contact Us
                </Link>{' '}
                page and include "DMCA" in the subject line of your message.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Privacy Policy Page Component
function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-3xl">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
            
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 mb-4">
                <strong>Last Updated:</strong> January 2026
              </p>
              
              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Information We Collect</h2>
              <p className="text-slate-600 mb-4">
                We collect minimal information to provide our services:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 mb-4">
                <li>Search queries to improve our service</li>
                <li>Basic usage analytics (anonymous)</li>
                <li>IP addresses for security and rate limiting purposes</li>
              </ul>
              
              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">How We Use Information</h2>
              <p className="text-slate-600 mb-4">
                The information we collect is used to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 mb-4">
                <li>Provide and improve our search services</li>
                <li>Prevent abuse and maintain security</li>
                <li>Analyze usage patterns to enhance user experience</li>
              </ul>
              
              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Data Security</h2>
              <p className="text-slate-600 mb-4">
                We implement appropriate security measures to protect against unauthorized access, 
                alteration, disclosure, or destruction of your information.
              </p>
              
              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Third-Party Services</h2>
              <p className="text-slate-600 mb-4">
                We may use third-party APIs to provide search functionality. These services have 
                their own privacy policies governing how they collect and use data.
              </p>
              
              <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Contact</h2>
              <p className="text-slate-600">
                For privacy concerns, please visit our{' '}
                <Link to="/contact" className="text-slate-900 font-semibold hover:underline">
                  Contact Us
                </Link>{' '}
                page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Recent Searches Page Component
function RecentSearchesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [recentBooks, setRecentBooks] = useState([]);
  const [trendingBooks, setTrendingBooks] = useState([]);
  const [recentPage, setRecentPage] = useState(parseInt(searchParams.get('recent_page')) || 1);
  const [trendingPage, setTrendingPage] = useState(parseInt(searchParams.get('trending_page')) || 1);
  const [recentTotal, setRecentTotal] = useState(0);
  const [trendingTotal, setTrendingTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [recentRes, trendingRes] = await Promise.all([
          fetch(`/api/books/recent?page=${recentPage}`),
          fetch(`/api/books/trending?page=${trendingPage}`)
        ]);

        const recentData = await recentRes.json();
        const trendingData = await trendingRes.json();

        setRecentBooks(recentData.books || recentData);
        setRecentTotal(recentData.totalPages || 0);
        setTrendingBooks(trendingData.books);
        setTrendingTotal(trendingData.totalPages);
      } catch (err) {
        console.error('Error fetching books:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [recentPage, trendingPage]);

  const handleRecentPageChange = (page) => {
    setRecentPage(page);
    setSearchParams({ recent_page: page, trending_page: trendingPage });
  };

  const handleTrendingPageChange = (page) => {
    setTrendingPage(page);
    setSearchParams({ recent_page: recentPage, trending_page: page });
  };

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-1 mt-4 flex-wrap">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
        >
          Prev
        </button>
        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-2 py-1 rounded border text-xs ${
              page === currentPage
                ? 'bg-slate-900 text-white border-slate-900'
                : 'border-slate-300 hover:bg-slate-100'
            }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
        >
          Next
        </button>
      </div>
    );
  };

  const BookList = ({ books, showViewCount = false }) => {
    if (books.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500 text-sm">
          No books yet
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {books.map((book, index) => {
          // Truncate title to max 40 chars
          const title = book.title 
            ? (book.title.length > 40 ? book.title.substring(0, 40) + '...' : book.title)
            : 'Unknown Title';
          
          // Truncate author to max 30 chars
          const author = book.author
            ? (book.author.length > 30 ? book.author.substring(0, 30) + '...' : book.author)
            : 'Unknown Author';

          // Format view count
          const viewCount = book.recent_views || book.total_views || book.view_count || 0;

          return (
            <Link
              key={index}
              to={`/download-pdf/${book.isbn}`}
              className="block bg-slate-50 border border-slate-200 rounded p-2 hover:bg-slate-100 hover:border-slate-400 transition-all group"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-slate-900 text-sm group-hover:text-slate-700">
                    {book.isbn}
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {title} &middot; {author}
                  </div>
                </div>
                {showViewCount && (
                  <div className="text-xs text-slate-500 whitespace-nowrap flex-shrink-0">
                    {viewCount.toLocaleString()} views
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading books...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-7xl">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Recent Searches</h1>
            <p className="text-slate-600">Browse recently viewed and trending textbooks</p>
          </div>

          {/* Two column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recently Viewed Column */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Recently Viewed</h2>
              <BookList books={recentBooks} showViewCount={false} />
              <Pagination 
                currentPage={recentPage} 
                totalPages={recentTotal} 
                onPageChange={handleRecentPageChange}
              />
            </div>

            {/* Trending Column */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Trending This Week</h2>
              <BookList books={trendingBooks} showViewCount={true} />
              <Pagination 
                currentPage={trendingPage} 
                totalPages={trendingTotal} 
                onPageChange={handleTrendingPageChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 404 Not Found Page
function NotFoundPage() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
            <div className="text-6xl font-bold text-slate-200 mb-4">404</div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              Page Not Found
            </h1>
            <p className="text-slate-600 mb-6">
              The page you're looking for doesn't exist or has been moved.
            </p>
            <div className="space-y-3">
              <a
                href="/"
                className="block w-full bg-slate-900 text-white py-2.5 px-4 rounded-lg hover:bg-slate-800 transition-colors font-medium"
              >
                Go to homepage
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main App with Router
export default function TextbookSearch() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<SearchPage />} />
        <Route path="/results" element={<ResultsPage />} />
		<Route path="/download-pdf/:bookId" element={<DownloadPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/dmca" element={<DMCAPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/recent" element={<RecentSearchesPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}